<template>
  <!-- 产品字段配置模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              列表字段配置
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              自定义产品列表的字段显示和排序
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 配置说明和操作按钮 -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 bg-blue-50 rounded-lg hidden sm:flex">
          <div class="flex-1">
            <div class="text-sm text-blue-800 space-y-1">
                 <div>• 拖拽调整字段显示顺序</div>
                 <div>• 开关控制字段显示/隐藏</div>
                 <div>• 设置列宽优化显示效果</div>
               </div>
            </div>
          
          <div class="flex items-center space-x-3 ml-6">
              <button
                @click="handleInitialize"
                :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                重置配置
              </button>
              <button
                @click="handleSave"
              :disabled="!hasChanges || submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {{ submitting ? '保存中...' : '保存配置' }}
              </button>
            </div>
          </div>

        <!-- 字段配置列表 -->
        <div class="space-y-3">

          <!-- 表格头部 (桌面端) -->
          <div class="hidden sm:grid grid-cols-10 gap-4 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">类型</div>
            <div class="col-span-2 text-center">列宽</div>
            <div class="col-span-2 text-center">显示</div>
          </div>

          <!-- 移动端表格头部 -->
          <div class="grid grid-cols-6 gap-2 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200 sm:hidden">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">显示</div>
              </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-12">
            <!-- Loading Spinner -->
            <div class="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
            <div class="text-sm text-gray-600">加载中...</div>
            </div>

          <!-- 字段配置项 -->
              <draggable
            v-else
                v-model="fieldConfigs"
            group="fieldConfigs"
            @end="handleDragChange"
                item-key="id"
            class="space-y-2"
              >
                <template #item="{ element: config }">
              <div>
                <!-- 桌面端布局 -->
                <div class="hidden sm:grid grid-cols-10 gap-4 items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center">
                    <div class="cursor-move text-gray-400 hover:text-gray-600">
                      <Bars3Icon class="h-5 w-5" />
                        </div>
                      </div>

                      <!-- 字段名称 -->
                  <div class="col-span-3">
                        <div class="flex items-center space-x-1">
                            <input
                              v-model="config.field_label"
                              type="text"
                        class="w-24 justify-end text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                            <span 
                              v-if="config.is_system_field"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              系统
                            </span>
                      <span 
                        v-else 
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                      >
                                自定义
                              </span>
                        </div>
                      </div>

                      <!-- 字段类型 -->
                  <div class="col-span-2 text-center">
                    <span class="text-sm text-gray-600">
                          <template v-if="config.is_system_field">
                            {{ getSystemFieldType(config.field_name) }}
                          </template>
                          <template v-else>
                            {{ getCustomFieldType(config.custom_field_info?.field_type) }}
                          </template>
                        </span>
                      </div>

                  <!-- 列宽设置 -->
                  <div class="col-span-2 flex justify-center">
                    <input
                      v-model.number="config.column_width"
                      type="number"
                      min="60"
                      max="500"
                      class="w-20 text-sm text-center border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                      </div>

                      <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="inline-flex items-center">
                          <input
                            v-model="config.is_visible"
                            type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                        </label>
                      </div>
                </div>

                <!-- 移动端布局 -->
                <div class="grid grid-cols-6 gap-2 items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors sm:hidden">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center">
                    <div class="cursor-move text-gray-400 hover:text-gray-600">
                      <Bars3Icon class="h-4 w-4" />
                    </div>
                      </div>

                  <!-- 字段名称 -->
                  <div class="col-span-3">
                    <div class="space-y-1">
                          <input
                        v-model="config.field_label"
                        type="text"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                      <div class="flex items-center space-x-1">
                        <span 
                          v-if="config.is_system_field" 
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          系统
                        </span>
                        <span 
                          v-else 
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                        >
                          自定义
                        </span>
                      </div>
                    </div>
                      </div>

                  <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="inline-flex items-center">
                      <input
                        v-model="config.is_visible"
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                    </div>
                  </div>
                </template>
              </draggable>

          <!-- 手机端操作按钮 -->
          <div class="sm:hidden flex flex-col space-y-3 pt-4">
            <div class="flex space-x-3">
              <button
                @click="handleInitialize"
                :disabled="submitting"
                class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon class="h-4 w-4 mr-2" />
                重置配置
              </button>
              <button
                @click="handleSave"
                :disabled="submitting || !hasChanges"
                class="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon class="h-4 w-4 mr-2" />
                {{ submitting ? '保存中...' : '保存配置' }}
              </button>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              @click="handleCancel"
              type="button"
              class="inline-flex items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { XMarkIcon, ArrowPathIcon, CheckIcon, Bars3Icon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import { productFieldConfigService } from '@/services/productService'
import draggable from 'vuedraggable'

const toast = useToast()

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// 响应式数据
const loading = ref(false)
const submitting = ref(false)
const fieldConfigs = ref([])
const originalConfigs = ref([])

// 计算属性
const hasChanges = computed(() => {
  if (!originalConfigs.value.length || !fieldConfigs.value.length) {
    return false
  }
  
  return JSON.stringify(fieldConfigs.value) !== JSON.stringify(originalConfigs.value)
})

// 系统字段类型映射
const systemFieldTypes = {
  image: '图片',
  name: '文本',
  code: '文本',
  brand: '文本',
  group: '分组',
  description: '文本',
  creator: '文本',
  created_at: '日期时间',
  is_active: '布尔值',
  actions: '操作'
}

// 自定义字段类型映射
const customFieldTypes = {
  text: '文本',
  textarea: '多行文本',
  number: '数字',
  decimal: '小数',
  date: '日期',
  datetime: '日期时间',
  boolean: '布尔值',
  choice: '单选',
  multiple_choice: '多选',
  url: '网址',
  email: '邮箱',
  phone: '电话'
}

// 方法
const getSystemFieldType = (fieldName) => {
  return systemFieldTypes[fieldName] || '未知'
}

const getCustomFieldType = (fieldType) => {
  return customFieldTypes[fieldType] || '未知'
}

const handleDragChange = () => {
  // 更新排序顺序
  fieldConfigs.value.forEach((config, index) => {
    config.sort_order = index + 1
  })
}

const loadFieldConfigs = async () => {
  loading.value = true
  try {
    const response = await productFieldConfigService.getFieldConfigs({
      ordering: 'sort_order'
    })
    
    if (response.success) {
      fieldConfigs.value = response.results || []
      originalConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
    } else {
      toast.error('获取字段配置失败')
    }
  } catch (error) {
    console.error('加载字段配置失败:', error)
    
    if (error.response?.status === 404) {
      // 如果没有配置，尝试初始化
      await handleInitialize()
    } else {
      toast.error('加载字段配置失败')
    }
  } finally {
    loading.value = false
  }
}

const handleInitialize = async () => {
  submitting.value = true
  try {
    const response = await productFieldConfigService.initializeFieldConfigs()
    
    if (response.success) {
      fieldConfigs.value = response.data || []
      originalConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
      toast.success('字段配置初始化成功')
    } else {
      toast.error('初始化字段配置失败')
    }
  } catch (error) {
    console.error('初始化字段配置失败:', error)
    toast.error('初始化字段配置失败')
  } finally {
    submitting.value = false
  }
}

const handleSave = async () => {
  if (!hasChanges.value) {
    return
  }
  
  submitting.value = true
  try {
    // 准备更新数据
    const updateData = fieldConfigs.value.map(config => ({
      id: config.id,
      field_label: config.field_label,
      is_visible: config.is_visible,
      sort_order: config.sort_order,
      column_width: config.column_width
    }))
    
    const response = await productFieldConfigService.bulkUpdateFieldConfigs(updateData)
    
    if (response.success) {
      originalConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
      toast.success('字段配置保存成功')
      emit('success')
    } else {
      toast.error('保存字段配置失败')
    }
  } catch (error) {
    console.error('保存字段配置失败:', error)
    toast.error('保存字段配置失败')
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  if (hasChanges.value) {
    if (confirm('有未保存的更改，确定要关闭吗？')) {
      // 恢复原始数据
      fieldConfigs.value = JSON.parse(JSON.stringify(originalConfigs.value))
      emit('close')
    }
  } else {
    emit('close')
  }
}

// 监听器
watch(() => props.show, (newVal) => {
  if (newVal) {
    loadFieldConfigs()
  }
}, { immediate: true })
</script>